import { useState, useContext } from 'react'
import CheckIcon from '@mui/icons-material/Check'
import DeleteIcon from '@mui/icons-material/Delete'
import WarningIcon from '@mui/icons-material/Warning'
import Checkbox from '@mui/material/Checkbox'
import Fab from '@mui/material/Fab'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import makeStyles from '@mui/styles/makeStyles'
import { motion } from 'framer-motion'
import pickBy from 'lodash/pickBy'
import { object, array, string, func } from 'prop-types'
import { useDispatch } from 'react-redux'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { CvsDrawer } from '@components'
import { modalContext } from '@contexts'
import { colors } from '@global/constants'
import { useAppSelector } from '@redux/hooks'
import { api } from 'api'
import { getCellContent, showSnackbar } from 'functions'
import { EditMTU } from './edit-mtu'
import { CvsTable } from '../../../components/table/cvs-table'
import { EnhancedTableToolbar } from '../../../components/table/enhanced-table-toolbar'
import { Fuzzy } from '../../../utils/fuse-search'
import { t } from '@lingui/core/macro'

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '100%',
    height: '100%',
  },
  paper: {
    width: '100%',
    overflow: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  table: {
    height: '100%',
    width: '100%',
    tableLayout: 'fixed',
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  textField: {
    width: 200,
  },
}))

export const MultiTargetUpload = ({
  multiTargets = [],
  adobeFields = [],
  fetchAdobeFields = () => {},
  accountId = '',
  target = {},
  realms = [],
}) => {
  const classes = useStyles()
  const ModalContext = useContext(modalContext)
  const [visible, setVisible] = useState(null)
  const [selected, setSelected] = useState({})
  const [displayMultiTargets, setDisplayMultiTargets] = useState([...multiTargets])
  const [DATASETS_ENABLED = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.DATASETS_PAGE]),
  )
  const dispatch = useDispatch()
  const [search, setSearch] = useState('')
  const [selectedMTU, setSelectedMTU] = useState(null)

  const columns = [
    { label: 'Report Suite', width: '40%', lookup: 'remote_realm_name' },
    { label: 'Report', type: 'date', width: '40%', lookup: 'remote_target_name' },
    { label: 'Status', width: '20%', lookup: 'status' },
  ]

  const onClose = () => {
    setVisible(false)
    setSelectedMTU(null)
  }

  const filterChanged = search => {
    setSearch(search)

    if (!search) {
      setDisplayMultiTargets(multiTargets)
    } else {
      setDisplayMultiTargets(fuzzySearch(search))
    }
  }

  const fuzzySearch = search => {
    const fuzzy = new Fuzzy(multiTargets, { keys: ['name'] })
    return fuzzy.search(search)
  }

  const editItems = () => {
    const item = Object.keys(pickBy(selected, row => row))[0]
    setSelectedMTU(displayMultiTargets.find(f => f.id == item))
    setVisible(true)
  }

  const addItem = () => {
    setSelectedMTU({ name: '', description: '' })
    setVisible(true)
  }

  const deleteItems = payload => {
    api
      .post(`/api/v1/account/targets/${target.id}/multi-targets/bulk/delete`, {
        multi_target_ids: Object.keys(payload),
      })
      .then(() => {
        const filteredmultiTargets = multiTargets.filter(f => !payload[f.local_multi_target_id])
        setDisplayMultiTargets(filteredmultiTargets)
        setSelected({})
      })
  }

  const updateMTUs = updated => {
    setSelectedMTU(Object.values(updated))
    saveMTUs(Object.values(updated))
    // why they still show in the gird though now?
  }

  const saveMTUs = mtus => {
    const payload = {
      target_id: target.id,
      multiTargets: mtus.map(mtu => {
        return {
          status: true,
          target_id: target.id,
          remote_realm_id: mtu.remote_realm_id,
          remote_target_id: mtu.id,
          remote_realm_name: mtu.remote_target_name,
          remote_target_name: mtu.remote_target_name,
        }
      }),
    }
    api
      .post(`/api/v1/account/targets/${target.id}/multi-targets`, payload)
      .then(() => {
        setVisible(false)
        setDisplayMultiTargets([...displayMultiTargets, ...payload.multiTargets])
        dispatch(
          showSnackbar({
            message: 'Multi Targets Updated',
            type: 'SUCCESS',
            duration: 5000,
          }),
        )
      })
      .catch(() => {
        dispatch(
          showSnackbar({
            message: 'Multi Targets failed to update.',
            type: 'ERROR',
            duration: 5000,
          }),
        )
      })
  }

  const deleteItemsModal = () => {
    ModalContext.displayModal({
      title: 'Are you sure you want to delete these multiTargets?',
      description: `multiTargets are a key way to organize and share ${
        DATASETS_ENABLED ? 'datasets' : 'templates'
      }. Any templates contained within these multiTargets will be moved to an "Unassigned ${
        DATASETS_ENABLED ? 'Datasets' : 'Templates'
      }" folder.`,
      confirm: () => {
        return () => {
          deleteItems(selected)
        }
      },
    })
  }

  const numSelected = Object.values(selected).filter(val => !!val).length

  return (
    <motion.div
      className={classes.root}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
      style={{ overflow: 'hidden' }}
    >
      <div className={classes.table} style={{ height: 'calc(100% - 85px)' }}>
        <EnhancedTableToolbar
          term={search}
          numSelected={numSelected}
          filterChanged={filterChanged}
          addItem={addItem}
          editItems={editItems}
          deleteItems={deleteItems}
          noAdd={false}
          blockMultipleEdit={false}
          style={{ paddingLeft: 24 }}
        >
          <Tooltip title={t`Delete`} style={{ backgroundColor: colors.red.light, color: 'white' }}>
            <Fab
              color="inherit"
              size="small"
              aria-label="Add"
              className={classes.fab}
              onClick={deleteItemsModal}
            >
              <DeleteIcon />
            </Fab>
          </Tooltip>
        </EnhancedTableToolbar>
        <div style={{ height: 'calc(100% - 64px)' }}>
          <CvsTable
            columns={columns}
            rows={displayMultiTargets}
            orderBy="name"
            sortOrder="asc"
            keyField="id"
            noAdd={false}
            addItem={addItem}
            noPagination={true}
            style={{ maxWidth: '100%' }}
            multiSelect={true}
            selectAll={val => {
              setSelected(
                displayMultiTargets.reduce(
                  (memo, item) => ({
                    ...memo,
                    [item.local_multi_target_id]: val,
                  }),
                  {},
                ),
              )
            }}
          >
            {({ row }) => {
              return (
                <TableRow
                  hover
                  onClick={() => {
                    setSelected({
                      ...selected,
                      [row.local_multi_target_id]: !selected[row.local_multi_target_id],
                    })
                  }}
                  tabIndex={-1}
                  key={row.local_multi_target_id}
                  style={{ cursor: 'pointer', maxWidth: '100%', overflow: 'hidden' }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      onClick={e => {
                        e.stopPropagation()
                        setSelected({
                          ...selected,
                          [row.local_multi_target_id]: !selected[row.local_multi_target_id],
                        })
                      }}
                      checked={selected[row.local_multi_target_id]}
                    />
                  </TableCell>
                  <TableCell
                    component="th"
                    scope="row"
                    padding="normal"
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {getCellContent('text', row.remote_realm_name)}
                  </TableCell>
                  <TableCell
                    component="th"
                    scope="row"
                    padding="normal"
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {getCellContent('text', row.remote_target_name)}
                  </TableCell>
                  <TableCell
                    component="th"
                    scope="row"
                    padding="normal"
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {row.status ? (
                      <CheckIcon style={{ color: colors.green.main }} />
                    ) : (
                      <WarningIcon style={{ color: colors.red.main }} />
                    )}
                  </TableCell>
                </TableRow>
              )
            }}
          </CvsTable>
        </div>
        <CvsDrawer
          visible={Boolean(visible)}
          onClose={onClose}
          title={`Edit ${(selectedMTU && selectedMTU.name) || 'Multi-Target'}`}
        >
          <EditMTU
            updateMTUs={updateMTUs}
            multiTargets={multiTargets}
            target={target}
            onClose={onClose}
            realms={realms}
            adobeFields={adobeFields}
            fetchAdobeFields={fetchAdobeFields}
            accountId={accountId}
          />
        </CvsDrawer>
      </div>
    </motion.div>
  )
}

MultiTargetUpload.propTypes = {
  multiTargets: array,
  adobeFields: array,
  fetchAdobeFields: func,
  accountId: string,
  target: object,
  realms: array,
}
